/**
 * 万里汇合作落地页 —— 句中强调渲染器（<strong> 白名单解析）
 *
 * 词条形如 `Receive funds in <strong>20+ currencies</strong> with zero fees`，
 * 本组件把它解析成 React 子节点 `['Receive funds in ', <strong>20+ currencies</strong>, ' with zero fees']`。
 *
 * 【为什么不用 dangerouslySetInnerHTML】—— 260818 实测踩到的真实 bug：
 * **React 在 hydration 阶段不校验、也不回填 `dangerouslySetInnerHTML` 的内容**，它默认服务端那段
 * HTML 是对的，直接沿用。而本页 SSR 走 i18nText 的 fallback.t（返回英文源）、客户端才拿到译文，
 * 两边内容本就不同 —— 于是 innerHTML 永远停在英文。之后 constants 被 useMemo 缓存、前后两次渲染
 * 的 `__html` 完全相同，React 判定无变化，也就再没有机会更新。
 * 实测（zh，1440px）：8 处普通文本节点全部正确变成中文，7 处 dangerouslySetInnerHTML 全部停留在英文，
 * 相关性 100%。改成 React 子节点后走的就是那条已被证明可用的普通渲染路径。
 *
 * 顺带解决了另外两件事：
 * 1) XSS：译文即便被注入 `<img onerror=...>`（实测在 innerHTML 下会执行），本组件只认 <strong>，
 *    其余一律当**纯文本**渲染，不会变成可执行标记。
 * 2) 结构可控：解析结果是文本与 <strong> 交替，DOM 与原型逐字一致，不会多出包裹元素
 *    （Hero 的 .cjwf-dark-benefit 是 flex，多一层就会改变换行点）。
 *
 * 【为什么解析要宽容】翻译引擎会改标签形态。260818 对 16 个语种 592 个样本实测：
 *   严格 `<strong>` 匹配 53.9%；放宽大小写与内部空格后可用率升到 67.7%。
 *   常见变形：`<Strong>`(94) `< Strong>`(13) `< strong>`(4)。
 * 剩下 32.3% 是不可救的（ar/tk 两个语种 0/37，尖括号退化成引号如 `"strong>...">/strong>`，
 * 或标签名被整个翻译成 `<Tekli>`/`<gut>`）。对这些，本组件保证**文字内容不丢**，
 * 只是没有加粗；并尽量清掉残留的标签碎片，避免用户看到 `>/strong>` 这类垃圾。
 *
 * 【SSR 安全】纯函数式渲染，不访问 window/document。
 */
import React from 'react';

/** 成对且可识别的强调段（大小写不敏感、容忍标签内空格） */
const PAIR = /<\s*strong\s*>([\s\S]*?)<\s*\/\s*strong\s*>/gi;

/**
 * 清理不成对的标签碎片，避免用户看到 `>/strong>`、`>>` 这类垃圾。
 *
 * 覆盖 260818 在 16 个语种译文里实测到的三类残骸：
 *   ① 引号/尖括号包裹的"标签样"片段 —— 标签名可能**已被翻译**，所以不能只匹配 strong：
 *      ar 把它译成 `> قوي>`、tk 译成 `<Tekli>`、de 个别条目译成 `<gut>`。故标签名放宽为
 *      "不含空白与括号的 1~16 个字符"。
 *   ② 连续的尖括号 run（`>>`、`<<`）—— ar 大量出现，是尖括号被引擎重复/错位的产物。
 *   ③ 形态正常但不成对的 `<strong>` / `</strong>`（另一半被吞掉）。
 *
 * 【为什么敢放宽到"任意标签名"】本页词条里**不存在**合法的尖括号文本（CASE-N6 断言词条内
 * 只能有 <strong>），所以清理后的文本里任何尖括号都是残骸。实测校验：加强前后，
 * zh/de/fr/ru/th 这 5 个译文干净的语种输出**逐字未变**（0 条差异），
 * 而残骸样本从 72 降到 27（ar 19→11、tk 15→2、ky 6→0、pt 2→0…）。
 * 长度上限 16 是为了不把正文里两个相距很远的尖括号误当成一对包裹。
 */
const RESIDUE = new RegExp(
  [
    '["\'<>]\\s*/?\\s*[^\\s<>"\']{1,16}\\s*["\'<>]', // "strong>  > قوي>  <Tekli>
    '[<>]{2,}', //                                      >>  <<
    '<\\s*/?\\s*[^\\s<>]{1,16}\\s*>', //                <strong> </strong> <gut>
    '/\\s*[^\\s<>/]{1,16}\\s*>', //                     /strong>  /قوي>（闭合标签丢了前面的 '<'）
  ].join('|'),
  'gi'
);

/**
 * @param {Object} props
 * @param {string} props.text 含 <strong> 标记的整句（i18next 取到的译文或英文源）
 * @returns {Array|null} React 子节点数组；无内容时返回 null
 */
export default function EmphasisText({ text }) {
  if (!text) return null;

  const src = String(text);
  const nodes = [];
  let last = 0;
  let m;

  PAIR.lastIndex = 0;
  while ((m = PAIR.exec(src)) !== null) {
    if (m.index > last) nodes.push({ em: false, s: src.slice(last, m.index) });
    nodes.push({ em: true, s: m[1] });
    last = m.index + m[0].length;
  }
  if (last < src.length) nodes.push({ em: false, s: src.slice(last) });

  return nodes
    .map((n) => (n.em ? n : { em: false, s: n.s.replace(RESIDUE, '') }))
    // 空片段必须丢掉：留着会产生**相邻文本节点**，而本仓 SSR 产物会被剥掉 HTML 注释，
    // React 正是靠 `<!-- -->` 分隔相邻文本节点 —— 剥掉后 hydrate 会报 Text content did not match
    // （判据见单测 CASE-R4）。
    .filter((n) => n.s !== '')
    .map((n, i) =>
      n.em
        ? React.createElement('strong', { key: i }, n.s)
        : React.createElement(React.Fragment, { key: i }, n.s)
    );
}
